import { client } from '../../../../../actions';
import { authHeader } from '../../../../../helpers/auth-header';
import {
  normalizeChildSchedules,
  normalizeParentSchedules,
  normalizeServiceDetails,
  normalizeServiceNames,
  normalizeServicesWithTree,
  normalizeWorkScheduleAddressDirectory,
} from './workScheduleApiMappers';
import {
  getChildWorkScheduleConfigurationPayload,
  getChildWorkScheduleCreatePayload,
  getChildWorkScheduleServiceNameUpdatePayload,
} from './workScheduleApiPayloads';

const workScheduleUrl = '/WorkSchedule';
const workSchedulePeriodUrl = `${workScheduleUrl}/period`;
const workSchedulePeriodCheckUrl = `${workScheduleUrl}/check_period`;
const workSchedulePeriodServiceNameUrl = `${workScheduleUrl}/period-service_name`;
const workSchedulePeriodConfigurationUrl = `${workSchedulePeriodUrl}/configuration`;
const workScheduleServiceNameUrl = `${workScheduleUrl}/service_name`;
const workScheduleServiceDetailsUrl = `${workScheduleUrl}/Services`;
const workScheduleServicesWithTreeUrl = `${workScheduleUrl}/services_with_tree`;
const workScheduleAddressDirectoryUrl = `${workScheduleUrl}/city_regions_with_streets_and_houses`;

const getWorkScheduleListData = (response, fallbackMessage) => {
  if (response?.success === false) {
    throw new Error(response.message || fallbackMessage);
  }

  return Array.isArray(response?.data) ? response.data : [];
};

export const fetchWorkScheduleCities = async ({ companyId }) => {
  const response = await client.get('/HousingStock/get_cities', {
    headers: authHeader(),
    params: { serviceCompanyId: companyId },
  });

  return response.data;
};

export const fetchWorkScheduleAddressDirectory = async ({
  companyId,
  cityId,
}) => {
  const response = await client.get(workScheduleAddressDirectoryUrl, {
    headers: authHeader(),
    params: { cityId, companyId },
  });
  const cityRegions = getWorkScheduleListData(
    response.data,
    'Не удалось загрузить адреса'
  );

  return normalizeWorkScheduleAddressDirectory(cityRegions);
};

export const fetchWorkScheduleExecuters = async ({ companyId }) => {
  const response = await client.get(
    `${workScheduleUrl}/executers_by_company_id`,
    {
      headers: authHeader(),
      params: { companyId },
    }
  );

  return getWorkScheduleListData(
    response.data,
    'Не удалось загрузить исполнителей'
  );
};

export const fetchParentWorkSchedules = async ({ companyId }) => {
  const response = await client.get(workScheduleUrl, {
    headers: authHeader(),
    params: { companyId },
  });

  return normalizeParentSchedules(response.data);
};

export const createParentWorkSchedule = async ({ companyId, name }) => {
  const response = await client.post(
    workScheduleUrl,
    {
      serviceCompanyId: Number(companyId),
      workScheduleName: name.trim(),
    },
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

export const updateParentWorkSchedule = async ({
  workScheduleId,
  workScheduleName,
}) => {
  const response = await client.put(
    workScheduleUrl,
    {
      workScheduleId,
      workScheduleName: workScheduleName.trim(),
    },
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

export const deleteParentWorkSchedule = async ({ companyId, id }) => {
  const response = await client.delete(workScheduleUrl, {
    headers: authHeader(),
    params: {
      workScheduleId: id,
      companyId,
    },
  });

  return response.data;
};

export const fetchChildWorkSchedules = async ({ workScheduleId }) => {
  const response = await client.get(workSchedulePeriodUrl, {
    headers: authHeader(),
    params: { workScheduleId },
  });

  return normalizeChildSchedules(response.data);
};

export const checkWorkSchedulePeriod = async ({
  workScheduleId,
  periodMonth,
  periodYear,
}) => {
  const response = await client.get(workSchedulePeriodCheckUrl, {
    headers: authHeader(),
    params: {
      workScheduleId: Number(workScheduleId),
      periodMonth: Number(periodMonth),
      periodYear: Number(periodYear),
    },
  });

  return response.data;
};

export const fetchWorkScheduleServiceNames = async ({ companyId }) => {
  const response = await client.get(workScheduleServiceNameUrl, {
    headers: authHeader(),
    params: { companyId },
  });

  return normalizeServiceNames(response.data);
};

export const fetchWorkScheduleServiceDetails = async ({
  workScheduleServiceNameId,
}) => {
  const response = await client.get(workScheduleServiceDetailsUrl, {
    headers: authHeader(),
    params: { workScheduleServiceNameId },
  });

  return normalizeServiceDetails(response.data);
};

export const fetchWorkScheduleServicesWithTree = async ({
  workScheduleServiceNameId,
}) => {
  const response = await client.get(workScheduleServicesWithTreeUrl, {
    headers: authHeader(),
    params: { workScheduleServiceNameId },
  });

  return normalizeServicesWithTree(response.data);
};

export const createWorkScheduleServiceSet = async ({
  companyId,
  serviceSetName,
  serviceSetComment = '',
  requestParentTypeId,
  requestTypeId,
}) => {
  const response = await client.post(
    workScheduleServiceNameUrl,
    {
      serviceCompanyId: Number(companyId),
      workScheduleServiceName: serviceSetName.trim(),
      serviceNameComment: serviceSetComment,
      requestParentTypeId,
      requestTypeId,
    },
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

export const updateWorkScheduleServiceSet = async ({
  companyId,
  id,
  serviceSetName,
  serviceSetComment = '',
  requestParentTypeId,
  requestTypeId,
}) => {
  const response = await client.put(
    workScheduleServiceNameUrl,
    {
      serviceCompanyId: Number(companyId),
      workScheduleServiceNameId: id,
      workScheduleServiceNameName: serviceSetName.trim(),
      workScheduleServiceNameComment: serviceSetComment,
      requestParentTypeId,
      requestTypeId,
    },
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

export const deleteWorkScheduleServiceSet = async ({ id }) => {
  const response = await client.delete(workScheduleServiceNameUrl, {
    headers: authHeader(),
    params: {
      workScheduleServiceId: id,
    },
  });

  return response.data;
};

export const createChildWorkSchedule = async (schedule) => {
  const response = await client.post(
    workSchedulePeriodUrl,
    getChildWorkScheduleCreatePayload(schedule),
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

const updateChildWorkScheduleServiceName = async (schedule) => {
  const response = await client.put(
    workSchedulePeriodServiceNameUrl,
    getChildWorkScheduleServiceNameUpdatePayload(schedule),
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

const updateChildWorkScheduleConfiguration = async (schedule) => {
  const response = await client.put(
    workSchedulePeriodConfigurationUrl,
    getChildWorkScheduleConfigurationPayload(schedule),
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

export const updateChildWorkSchedule = async (schedule) => {
  const serviceNameResponse = await updateChildWorkScheduleServiceName(
    schedule
  );

  if (serviceNameResponse?.success === false) {
    return serviceNameResponse;
  }

  return updateChildWorkScheduleConfiguration(schedule);
};

export const deleteChildWorkSchedule = async ({ id }) => {
  const response = await client.delete(workSchedulePeriodUrl, {
    headers: authHeader(),
    params: {
      workSchedulePeriodId: id,
    },
  });

  return response.data;
};
